import { useMemo } from "react";
import { intlFormat } from "date-fns";
import { useGetTranslatedDay } from "./use-get-translated-day";
import type { OpeningHoursByDay } from "@/lib/opening-hours";

export const useFormattedOpeningHours = (openingHoursData: OpeningHoursByDay) => {
  const { getTranslatedDay } = useGetTranslatedDay();

  const groupedHours = useMemo(() => {
    return Object.entries(openingHoursData).reduce(
      (acc: Record<string, Record<string, number[]>>, [dayStr, hours]) => {
        const day = Number(dayStr);
        for (const [orderType, hour] of Object.entries(hours || {})) {
          if (!hour) continue;
          const startTimeKey = intlFormat(
            hour.startTime,
            {
              hour: "2-digit",
              minute: "2-digit",
            },
            {
              locale: "nl",
            }
          );
          const endTimeKey = intlFormat(
            hour.endTime,
            {
              hour: "2-digit",
              minute: "2-digit",
            },
            {
              locale: "nl",
            }
          );
          const timeKey = `${startTimeKey} - ${endTimeKey}`;
          if (!acc[orderType]) {
            acc[orderType] = {};
          }
          if (!acc[orderType][timeKey]) {
            acc[orderType][timeKey] = [];
          }
          acc[orderType][timeKey].push(day);
        }
        return acc;
      },
      {}
    );
  }, [openingHoursData]);

  const getFirstDaySortKey = useMemo(() => (days: number[]) => {
    if (days.length === 0) return 0;
    const firstDay = [...days]
      .map(day => ({
        day,
        sortKey: day === 0 ? 7 : day
      }))
      .sort((a, b) => a.sortKey - b.sortKey)
      .map(item => item.day)[0];
    return firstDay === 0 ? 7 : firstDay;
  }, []);

  const sortedGroupedHours = useMemo(() => {
    return Object.fromEntries(
      Object.entries(groupedHours).map(([orderType, timeGroups]) => [
        orderType,
        Object.fromEntries(
          Object.entries(timeGroups).sort(([, daysA], [, daysB]) => 
            getFirstDaySortKey(daysA) - getFirstDaySortKey(daysB)
          )
        )
      ])
    );
  }, [groupedHours, getFirstDaySortKey]);

  const formatDays = useMemo(() => (days: number[]) => {
    if (days.length === 0) return "";
    const sorted = [...days].map(day => ({
      day,
      sortKey: day === 0 ? 7 : day
    })).sort((a, b) => a.sortKey - b.sortKey).map(item => item.day);
    const ranges: string[] = [];
    let start = sorted[0];
    let prev = sorted[0];
    for (let i = 1; i < sorted.length; i++) {
      const isConsecutive = sorted[i] === prev + 1 || (prev === 6 && sorted[i] === 0);
      if (isConsecutive) {
        prev = sorted[i];
      } else {
        if (start === prev) {
          ranges.push(getTranslatedDay(start));
        } else {
          ranges.push(`${getTranslatedDay(start)} - ${getTranslatedDay(prev)}`);
        }
        start = sorted[i];
        prev = sorted[i];
      }
    }
    if (start === prev) {
      ranges.push(getTranslatedDay(start));
    } else {
      ranges.push(`${getTranslatedDay(start)} - ${getTranslatedDay(prev)}`);
    }
    return ranges.join(", ");
  }, [getTranslatedDay]);

  return {
    sortedGroupedHours,
    formatDays,
  };
};
